import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

import { IBread } from '@components/Breadcrumb/BreadCrumb';
import { CONST_EXPORT_INVOICE, CONST_LIST_INVOICE_COMPANY, CONST_OPERATOR_INVOICE } from 'constants/language';
import { createInvoicePDF } from 'helper/export';
import { useParams } from 'react-router';
import { formatDateCommon } from 'helper/date';
import { apiOperatorVerifyOTP, apiUpdateStatusAgencyInvoice } from 'api/operator';
import { responseCode, responseSuccess } from 'helper/responseSuccess/responseSuccess';
import { EStatusInvoiceAgency } from 'constants/constants';
import {
  IAgencyBasicOperatorInvoice,
  IDownTime,
  IFilterPage,
  IInvoiceDetailInvoiceSiteAgency,
  MAX_ITEM_PAGE_ONE_AGENCY,
  MAX_ITEM_PAGE_OTHER_AGENCY,
  defaultPageInvoiceDetailAgency,
} from 'constants/invoice';
import { useAgencyInvoiceDetail, useGetAgencyInfoInvoice } from '@hooks/useAgency';

const useInvoiceDetailAgency = () => {
  const { id } = useParams();
  const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState<boolean>(false);
  const [invoiceInfo, setInvoiceInfo] = useState<IAgencyBasicOperatorInvoice>();
  const [invoiceDetail, setInvoiceDetail] = useState<IInvoiceDetailInvoiceSiteAgency[]>();
  const [invoiceAllDetail, setInvoiceAllDetail] = useState<IInvoiceDetailInvoiceSiteAgency[]>();
  const [page, setPage] = useState<IFilterPage>(defaultPageInvoiceDetailAgency);
  const [isOpenOTP, setIsOpenOTP] = useState<boolean>(false);
  const [dateDownTime] = useState<IDownTime>();
  const [isLoadingSubmitOTP, setLoadingSubmitOTP] = useState<boolean>(false);
  const [otp, setOtp] = useState('');
  const [perPage, setPerPage] = useState<number>(page.per_page);

  const { data: dataInvoiceInfo, isLoading } = useGetAgencyInfoInvoice(Number(id));
  useEffect(() => {
    let max = MAX_ITEM_PAGE_ONE_AGENCY;
    if (dataInvoiceInfo?.operator_address1 && dataInvoiceInfo?.operator_address1.length > 23) max--; // 23 characters in one row
    if (dataInvoiceInfo?.operator_address2 && dataInvoiceInfo?.operator_address2.length > 23) max--; // 23 characters in one row
    setPerPage(max);
    setInvoiceInfo(dataInvoiceInfo);
  }, [dataInvoiceInfo]);

  const { data: dataInvoiceDetail, isLoading: isLoadingInvoiceDetail } = useAgencyInvoiceDetail(Number(id), {
    isLoading: isLoading,
    per_page: perPage,
    offset: page.offset,
  });

  const { data: dataInvoiceAllDetail, isLoading: isLoadingInvoiceAllDetail } = useAgencyInvoiceDetail(Number(id), {
    isLoading: isLoading,
    per_page: page.total,
  });

  useEffect(() => {
    setInvoiceDetail(dataInvoiceDetail?.data);
    setPage((_perPage) => ({
      ..._perPage,
      total: dataInvoiceDetail?.total ? dataInvoiceDetail.total : 0,
    }));
  }, [dataInvoiceDetail]);

  useEffect(() => {
    setInvoiceAllDetail(dataInvoiceAllDetail?.data);
  }, [dataInvoiceAllDetail]);

  const BREADS: IBread[] = [
    {
      name: CONST_LIST_INVOICE_COMPANY.LIST_INVOICE,
      path: '/agency-invoices',
    },
    {
      name: CONST_OPERATOR_INVOICE.AGENCY_INVOICE_DETAIL,
      path: '',
    },
  ];

  const exportInvoicePDF = useCallback(() => {
    if (isLoadingExport || !invoiceInfo) {
      return;
    }
    setIsLoadingExport(true);
    setTimeout(async () => {
      await createInvoicePDF(
        invoiceInfo.id,
        CONST_EXPORT_INVOICE.REQUEST_DATE + formatDateCommon(invoiceInfo?.request_date),
        CONST_EXPORT_INVOICE.EXPORT_FILE_NAME,
        true,
        true
      );
      setIsLoadingExport(false);
    }, 100);
  }, [invoiceInfo, isLoadingExport]);

  const changePaging = useCallback(
    (pageChange: number) => {
      if (pageChange > 1) {
        setPage({
          ...page,
          page: pageChange,
          per_page: MAX_ITEM_PAGE_OTHER_AGENCY,
          offset: MAX_ITEM_PAGE_ONE_AGENCY + MAX_ITEM_PAGE_OTHER_AGENCY * (pageChange - 2),
        });
      } else {
        setPage({
          ...page,
          page: pageChange,
          per_page: MAX_ITEM_PAGE_ONE_AGENCY,
          offset: 0,
        });
      }
    },
    [page]
  );

  const onSubmitOTP = async (otp: any) => {
    try {
      if (Number(id) && invoiceInfo) {
        if (otp.length === 6) {
          setLoadingSubmitOTP(true);
          const response = await apiOperatorVerifyOTP(Number(id), { otp: otp });
          if (responseSuccess(response)) {
            setIsOpenOTP(false);
            setInvoiceInfo({
              ...invoiceInfo,
              status: EStatusInvoiceAgency.WAITING_OPERATOR_VERIFIED,
            });
            message.success(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgVerifyOTPSccuess);
          } else {
            if (responseCode(response) === 'G001') {
              message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgVerifyOTPFalse);
            }
          }
        }
      }
    } catch (error) {
    } finally {
      setLoadingSubmitOTP(false);
    }
  };

  const confirmInvoice = useCallback(async () => {
    if (Number(id) && invoiceInfo) {
      try {
        setIsLoadingConfirm(true);
        const response = await apiUpdateStatusAgencyInvoice(Number(id), EStatusInvoiceAgency.OPERATOR_APPROVED);
        if (responseSuccess(response)) {
          setInvoiceInfo({
            ...invoiceInfo,
            status: EStatusInvoiceAgency.OPERATOR_APPROVED,
          });
          // if (response?.data && response?.data.status === EStatusInvoiceAgency.WAITING_OPERATOR_VERIFY) {
          //   setDateDownTime(response?.data);
          //   setIsOpenOTP(true);
          //   setOtp('');
          // }
        }
      } catch (error: any) {
        if (responseCode(error) === 'K001') {
          message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgCantConfirmInvoice);
        }
        if (responseCode(error) === 'K002') {
          message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgOperatorNotPhone);
        }
        if (responseCode(error) === 'K003') {
          message.error(CONST_OPERATOR_INVOICE.LIST_INVOICE_AGENCY_TABLE.msgAgencyNotSettingBank);
        }
      } finally {
        setIsLoadingConfirm(false);
      }
    }
  }, [id, invoiceInfo]);

  return {
    BREADS,
    invoiceDetail,
    page,
    changePaging,
    isLoading,
    exportInvoicePDF,
    invoiceAllDetail,
    isLoadingExport,
    invoiceInfo,
    confirmInvoice,
    isLoadingConfirm,
    isLoadingInvoiceAllDetail,
    onSubmitOTP,
    isOpenOTP,
    setIsOpenOTP,
    dateDownTime,
    isLoadingSubmitOTP,
    otp,
    setOtp,
    isLoadingInvoiceDetail,
  };
};

export default useInvoiceDetailAgency;
